import { setAuthCookies } from './apiService';
import { CHALO_API_BASE, AUTH_API_BASE } from '@/config/apiConfig';

/**
 * Registration Auth Service
 * 
 * Handles token management during the registration flow for both NewUser and ExistingUser.
 * This service separates concerns and makes the registration flow cleaner.
 */

export interface RegistrationTokens {
  accessToken: string;
  refreshToken: string;
  deviceId: string;
}

export interface UserProfileData {
  fn: string;
  ln: string;
  cc: string;
  lang: string;
  ut: string;
  g: string;
  refcc?: string; // Referred calling code
  refph?: string; // Referred phone number
}

export interface UserStoreData {
  fn: string;
  ln: string;
  lang: string;
  ut: string;
  ph: string;
  uId: number;
  cc: string;
  g: string;
  refph?: string; // Referred phone number (optional)
  refcc?: string; // Referred calling code (optional)
}

/**
 * Check if an error is an internet/connectivity-related error
 */
const isNetworkError = (error: any): boolean => {
  if (!error) return false;
  
  const errorMessage = error.message || error.toString() || '';
  const errorName = error.name || '';
  const errorCode = error.code || '';
  
  const internetErrorPatterns = [
    'Network request failed',
    'Failed to fetch',
    'NetworkError',
    'ERR_INTERNET_DISCONNECTED',
    'ERR_NETWORK_CHANGED',
    'ERR_CONNECTION_REFUSED',
    'ERR_CONNECTION_TIMED_OUT',
    'ERR_CONNECTION_RESET',
    'ECONNREFUSED',
    'ENOTFOUND',
  ];
  
  const messageMatch = internetErrorPatterns.some(pattern =>
    errorMessage.toLowerCase().includes(pattern.toLowerCase())
  );
  
  const nameMatch = internetErrorPatterns.some(pattern =>
    errorName.toLowerCase().includes(pattern.toLowerCase())
  );
  
  const codeMatch = internetErrorPatterns.some(pattern =>
    errorCode.toLowerCase().includes(pattern.toLowerCase())
  );
  
  return messageMatch || nameMatch || codeMatch;
};

/**
 * Delay execution for specified milliseconds
 */
const delay = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

/**
 * Make an authenticated API call during registration without using fetchWithToken
 * This bypasses cookie token lookup and uses the passed tokens directly
 */
export const fetchWithRegistrationToken = async (
  url: string,
  options: RequestInit,
  tokens: RegistrationTokens,
  language: string = 'en'
): Promise<Response> => {
  const requestOptions: RequestInit = {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${tokens.accessToken}`,
      'lang': language,
    },
  };
  
  console.log('[RegistrationAuth] Making authenticated request to:', url);
  
  // Helper function to make the actual fetch request
  const makeRequest = async (): Promise<Response> => {
    return await fetch(url, requestOptions);
  };
  
  // Attempt initial request with internet connectivity error handling
  let response: Response;
  
  try {
    response = await makeRequest();
  } catch (error) {
    // Check if it's an internet connectivity error
    if (isNetworkError(error)) {
      console.log('[RegistrationAuth] Network error detected on initial request. Waiting 2 seconds before retry...');
      
      // Wait 2 seconds before retry
      await delay(2000);
      
      try {
        // Retry the request
        console.log('[RegistrationAuth] Retrying request after network error...');
        response = await makeRequest();
      } catch (retryError) {
        // Retry also failed - throw a clear, user-friendly error
        console.error('[RegistrationAuth] Network error persisted after retry:', retryError);
        throw new Error('No internet. Reconnect to keep the conversation going.');
      }
    } else {
      // Not a network error, re-throw it
      throw error;
    }
  }
  
  // If 401, try to refresh token once
  if (response.status === 401) {
    console.log('[RegistrationAuth] Received 401, attempting token refresh...');
    try {
      const refreshedTokens = await refreshRegistrationToken(tokens);
      
      // Retry the request with new tokens
      const retryOptions: RequestInit = {
        ...requestOptions,
        headers: {
          ...requestOptions.headers,
          Authorization: `Bearer ${refreshedTokens.accessToken}`,
        },
      };
      
      console.log('[RegistrationAuth] Retrying request with refreshed token');
      try {
        response = await fetch(url, retryOptions);
      } catch (error) {
        // Check if it's a network error after token refresh
        if (isNetworkError(error)) {
          console.log('[RegistrationAuth] Network error after token refresh. Waiting 2 seconds before retry...');
          await delay(2000);
          try {
            response = await fetch(url, retryOptions);
          } catch (retryError) {
            console.error('[RegistrationAuth] Network error persisted after token refresh retry:', retryError);
            throw new Error('No internet. Reconnect to keep the conversation going.');
          }
        } else {
          throw error;
        }
      }
    } catch (error) {
      console.error('[RegistrationAuth] Token refresh failed:', error);
      // If it's the network error message, rethrow it
      if (error instanceof Error && error.message === 'No internet. Reconnect to keep the conversation going.') {
        throw error;
      }
      throw new Error('Authentication failed. Please try again.');
    }
  }
  
  return response;
};

/**
 * Refresh tokens during registration flow
 * Uses the auth server endpoint for token refresh
 */
export const refreshRegistrationToken = async (
  tokens: RegistrationTokens
): Promise<RegistrationTokens> => {
  console.log('[RegistrationAuth] Refreshing registration tokens...');
  
  const response = await fetch(`${AUTH_API_BASE}/api/auth/refresh-token`, {
    method: 'POST',
    headers: {
      'accessToken': tokens.accessToken,
      'refreshToken': tokens.refreshToken,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ deviceId: tokens.deviceId }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('[RegistrationAuth] Refresh failed:', response.status, errorText);
    throw new Error(`Token refresh failed: ${response.status}`);
  }

  const data = await response.json();
  const { accessToken, refreshToken } = data;

  if (!accessToken || !refreshToken) {
    console.error('[RegistrationAuth] Invalid refresh response - missing tokens');
    throw new Error('Invalid refresh token response');
  }

  console.log('[RegistrationAuth] Tokens refreshed successfully');
  
  return {
    accessToken,
    refreshToken,
    deviceId: tokens.deviceId,
  };
};

/**
 * Create/Update user profile during registration
 * POST /create-userprofile
 */
export const createUserProfile = async (
  tokens: RegistrationTokens,
  profileData: UserProfileData,
  language: string = 'en'
): Promise<any> => {
  console.log('[RegistrationAuth] Creating user profile...');
  console.log('[RegistrationAuth] Profile data:', profileData);
  
  try {
    const response = await fetchWithRegistrationToken(
      `${CHALO_API_BASE}/create-userprofile`,
      {
        method: 'POST',
        headers: {
          'accept': 'text/plain',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(profileData),
      },
      tokens,
      language
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[RegistrationAuth] Create profile failed:', response.status, errorText);
      throw new Error(`Failed to create profile: ${response.status}`);
    }

    const data = await response.json();
    console.log('[RegistrationAuth] Profile created successfully');
    return data;
  } catch (error) {
    console.error('[RegistrationAuth] Error creating user profile:', error);
    // Rethrow the specific network error message if it's the network error
    if (error instanceof Error && error.message === 'No internet. Reconnect to keep the conversation going.') {
      throw error;
    }
    throw error;
  }
};

/**
 * Store user profile during registration
 * POST /usersstore-profile
 */
export const storeUserProfile = async (
  tokens: RegistrationTokens,
  storeData: UserStoreData,
  language: string = 'en'
): Promise<any> => {
  console.log('[RegistrationAuth] Storing user profile...');
  console.log('[RegistrationAuth] POST /usersstore-profile request:', {
    url: `${AUTH_API_BASE}/usersstore-profile`,
    body: storeData,
    lang: language,
  });
  
  try {
    const response = await fetchWithRegistrationToken(
      `${AUTH_API_BASE}/usersstore-profile`,
      {
        method: 'POST',
        headers: {
          'accept': '*/*',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(storeData),
      },
      tokens,
      language
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[RegistrationAuth] Store profile failed:', response.status, errorText);
      throw new Error(`Failed to store profile: ${response.status}`);
    }

    const data = await response.json();
    console.log('[RegistrationAuth] POST /usersstore-profile response:', {
      status: response.status,
      ok: response.ok,
      data,
    });
    console.log('[RegistrationAuth] Profile stored successfully');
    return data;
  } catch (error) {
    console.error('[RegistrationAuth] Error storing user profile:', error);
    // Rethrow the specific network error message if it's the network error
    if (error instanceof Error && error.message === 'No internet. Reconnect to keep the conversation going.') {
      throw error;
    }
    throw error;
  }
};

/**
 * Handle post-registration token refresh
 * This is called after successful registration to ensure fresh tokens
 */
export const handlePostRegistrationRefresh = async (
  tokens: RegistrationTokens
): Promise<RegistrationTokens> => {
  console.log('[RegistrationAuth] Handling post-registration refresh...');
  
  try {
    const refreshedTokens = await refreshRegistrationToken(tokens);
    
    // Save refreshed tokens to cookies
    setAuthCookies({
      accessToken: refreshedTokens.accessToken,
      refreshToken: refreshedTokens.refreshToken,
      deviceId: refreshedTokens.deviceId,
    });
    
    console.log('[RegistrationAuth] Post-registration refresh completed');
    return refreshedTokens;
  } catch (error) {
    console.warn('[RegistrationAuth] Post-registration refresh failed:', error);
    // Don't throw - this is non-critical, return original tokens
    return tokens;
  }
};

